import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLiveData } from "@/context/LiveDataContext";
import { Dot } from "@/components/common";
import AccountModal from "@/components/AccountModal";
import logoImg from "@/assets/logo.png";
import {
  LayoutDashboard, Map, Plane, Send, SlidersHorizontal, ScrollText,
  PackageOpen, FlaskConical, LogOut, RefreshCw,
  KeyRound, ShieldAlert, Gamepad2, Server, Sparkles, History, Globe2,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/map", label: "Carte live", icon: Map, testid: "nav-map" },
  { to: "/drones", label: "Drones", icon: Plane, testid: "nav-drones" },
  { to: "/manual-control", label: "Pilotage manuel", icon: Gamepad2, testid: "nav-manual" },
  { to: "/assistant", label: "Assistant IA", icon: Sparkles, testid: "nav-assistant" },
  { to: "/missions", label: "Missions", icon: Send, testid: "nav-missions" },
  { to: "/flight-sessions", label: "Sessions de vol", icon: History, testid: "nav-flight-sessions" },
  { to: "/settings", label: "Paramètres de vol", icon: SlidersHorizontal, testid: "nav-settings" },
  { to: "/logs", label: "Logs", icon: ScrollText, testid: "nav-logs" },
  { to: "/drop", label: "Largage", icon: PackageOpen, testid: "nav-drop" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { apiConnected, fleetConnected, refresh } = useLiveData();
  const navigate = useNavigate();
  const [accountOpen, setAccountOpen] = useState(false);

  return (
    <div className="h-screen flex bg-[#0A0A0A] text-white overflow-hidden">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-[#0A0A0A] border-r border-white/10 flex flex-col">
        <div className="h-14 flex items-center gap-2 px-4 border-b border-white/10">
          <div className="group w-8 h-8 rounded-sm overflow-hidden flex items-center justify-center bg-black ring-1 ring-transparent shadow-[0_0_0_0_rgba(59,130,246,0)] transition-[box-shadow,box-shadow] duration-300 hover:ring-[#3B82F6]/60 hover:shadow-[0_0_14px_2px_rgba(59,130,246,0.55)]">
            <img src={logoImg} alt="SkyCore AI" className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-110" data-testid="sidebar-logo" />
          </div>
          <div>
            <div className="font-chivo font-black tracking-tighter uppercase leading-none">SkyCore AI</div>
            <div className="font-mono text-[8px] tracking-[0.3em] uppercase text-[#525252]">Drone Gateway</div>
          </div>
        </div>

        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {NAV.map((n) => (
            <NavItem key={n.to} {...n} />
          ))}
          {user?.role === "admin" && (
            <NavItem to="/drone-registry" label="Drones & Tokens" icon={KeyRound} testid="nav-drone-registry" />
          )}
          {user?.role === "admin" && (
            <NavItem to="/infrastructure" label="Infrastructure" icon={Server} testid="nav-infrastructure" />
          )}
          {user?.role === "admin" && (
            <NavItem to="/tests" label="Tests techniques" icon={FlaskConical} testid="nav-tests" />
          )}
          {user?.role === "admin" && (
            <NavItem to="/esri-qualification" label="Qualification Esri" icon={Globe2} testid="nav-esri-qualification" />
          )}
        </nav>

        <div className="p-3 border-t border-white/10">
          {user?.password_is_default && (
            <button data-testid="default-pw-warning" onClick={() => setAccountOpen(true)}
              className="w-full flex items-center gap-2 mb-2 font-mono text-[10px] text-[#F59E0B] border border-[#F59E0B]/40 bg-[#F59E0B]/10 rounded-sm px-2 py-1.5 text-left hover:bg-[#F59E0B]/20 transition-colors">
              <ShieldAlert size={13} className="shrink-0" /> Mot de passe par défaut — à changer
            </button>
          )}
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-sm bg-[#1f1f1f] border border-white/10 flex items-center justify-center font-mono text-xs">
              {(user?.name || "?")[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-xs truncate">{user?.name}</div>
              <div className="font-mono text-[9px] text-[#525252] uppercase tracking-wider">{user?.role}</div>
            </div>
            <button data-testid="account-btn" onClick={() => setAccountOpen(true)} title="Compte"
              className="w-7 h-7 flex items-center justify-center border border-white/10 rounded-sm hover:bg-white/10 text-[#A3A3A3]">
              <KeyRound size={13} />
            </button>
          </div>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }}
            className="w-full flex items-center gap-2 font-mono text-xs text-[#A3A3A3] hover:text-[#EF4444] border border-white/10 hover:border-[#EF4444]/40 rounded-sm px-3 py-2 transition-colors">
            <LogOut size={13} /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 shrink-0 bg-[#141414] border-b border-white/10 flex items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <StatusPill label="API REST" ok={apiConnected} testid="status-api" />
            <StatusPill label="WS Flotte" ok={fleetConnected} testid="status-ws" />
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="env-badge" className="font-mono text-[9px] tracking-[0.2em] uppercase border border-[#F59E0B]/40 text-[#F59E0B] rounded-sm px-2 py-1">
              Production
            </span>
            <button data-testid="refresh-btn" onClick={refresh}
              className="flex items-center gap-1.5 font-mono text-[10px] tracking-wider uppercase border border-white/15 hover:bg-white/10 rounded-sm px-3 py-1.5 transition-colors">
              <RefreshCw size={12} /> Rafraîchir
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      <AccountModal open={accountOpen} onClose={() => setAccountOpen(false)} />
    </div>
  );
}

function NavItem({ to, label, icon: Icon, testid, end }) {
  return (
    <NavLink to={to} end={end} data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-sm font-mono text-xs tracking-wide transition-colors ${
          isActive ? "bg-[#3B82F6]/15 text-white border-l-2 border-[#3B82F6]" : "text-[#A3A3A3] hover:bg-white/5 hover:text-white border-l-2 border-transparent"
        }`
      }>
      <Icon size={16} /> {label}
    </NavLink>
  );
}

function StatusPill({ label, ok, testid }) {
  const color = ok === null ? "#525252" : ok ? "#22C55E" : "#EF4444";
  return (
    <div data-testid={testid} className="flex items-center gap-2">
      <Dot color={color} pulse={!ok && ok !== null} />
      <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#A3A3A3]">
        {label}: <span style={{ color }}>{ok === null ? "..." : ok ? "OK" : "OFF"}</span>
      </span>
    </div>
  );
}
